/**
 * FVG Zone Primitive
 *
 * Renders Fair Value Gap (FVG) zones as semi-transparent filled rectangles
 * on the TradingView Lightweight Chart. Zones are drawn behind candles
 * and dimmed when filled.
 *
 * Zone types:
 * - Bullish FVG: Gap up (green) - price should return to fill
 * - Bearish FVG: Gap down (red) - price should return to fill
 * - IFVG: Inverse FVG after complete fill (dashed border)
 */

import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import { FVGZone, ZoneType, ICTTimeframe } from '@shared/types';

// Renderer data with pre-calculated coordinates
interface FVGZoneRendererData {
  x1: number;        // Left edge (start time)
  x2: number;        // Right edge (end time or current)
  y1: number;        // Top price coordinate
  y2: number;        // Bottom price coordinate
  zoneType: ZoneType;
  isInverse: boolean;
  isInvalidated: boolean;
  isFilled: boolean;
  fillPercentage: number;
  timeframe: ICTTimeframe;
  fillColor: string;
  borderColor: string;
  ageMinutes: number;
}

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Timeframe badge colors
const TIMEFRAME_COLORS: Record<ICTTimeframe, string> = {
  '2m': 'rgba(156, 163, 175, 0.8)',  // Gray
  '5m': 'rgba(59, 130, 246, 0.8)',   // Blue
  '15m': 'rgba(168, 85, 247, 0.8)',  // Purple
  '1h': 'rgba(249, 115, 22, 0.8)',   // Orange
  '4h': 'rgba(236, 72, 153, 0.8)',   // Pink
};

class FVGZonePaneRenderer implements IPrimitivePaneRenderer {
  private _data: FVGZoneRendererData[] = [];
  private _showTimeframeBadge: boolean = true;
  private _showInvalidatedBadges: boolean = true;
  private _srLabelYPositions: number[] = []; // CSS-pixel Y centers of S/R labels

  update(data: FVGZoneRendererData[], showTimeframeBadge: boolean, showInvalidatedBadges: boolean, srLabelYPositions: number[]): void {
    this._data = data;
    this._showTimeframeBadge = showTimeframeBadge;
    this._showInvalidatedBadges = showInvalidatedBadges;
    this._srLabelYPositions = srLabelYPositions;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const pixelRatio = scope.horizontalPixelRatio as number;
      const verticalPixelRatio = scope.verticalPixelRatio as number;

      for (const zone of this._data) {
        const x1 = zone.x1 * pixelRatio;
        const x2 = zone.x2 * pixelRatio;
        const y1 = zone.y1 * verticalPixelRatio;
        const y2 = zone.y2 * verticalPixelRatio;

        const width = x2 - x1;
        const height = y2 - y1;

        // Skip if zone is too small to render
        if (Math.abs(width) < 1 || Math.abs(height) < 1) continue;

        // Draw filled rectangle
        ctx.fillStyle = zone.fillColor;
        ctx.fillRect(x1, y1, width, height);

        // Draw border
        ctx.strokeStyle = zone.borderColor;
        ctx.lineWidth = 1 * pixelRatio;

        // Use dashed border for IFVG zones
        if (zone.isInverse) {
          ctx.setLineDash([4 * pixelRatio, 4 * pixelRatio]);
        } else {
          ctx.setLineDash([]);
        }

        ctx.strokeRect(x1, y1, width, height);
        ctx.setLineDash([]);

        // Draw fill percentage indicator if partially filled
        if (!zone.isFilled && zone.fillPercentage > 0 && zone.fillPercentage < 100) {
          const fillHeight = height * (zone.fillPercentage / 100);

          // For bullish zones, fill from bottom; for bearish, fill from top
          // Use the zone's own fillColor so the overlay respects the user-configured opacity
          // rather than rendering a hardcoded 0.25 alpha that overrides low-opacity settings.
          if (zone.zoneType === 'bullish') {
            ctx.fillStyle = zone.fillColor;
            ctx.fillRect(x1, y2 - fillHeight, width, fillHeight);
          } else {
            ctx.fillStyle = zone.fillColor;
            ctx.fillRect(x1, y1, width, fillHeight);
          }
        }

        // Draw timeframe badge on the right edge
        const showBadge = this._showTimeframeBadge
          && width > 20 * pixelRatio
          && (this._showInvalidatedBadges || !zone.isInvalidated);
        if (showBadge) {
          const badgeColor = TIMEFRAME_COLORS[zone.timeframe] || 'rgba(156, 163, 175, 0.8)';
          const badgeText = `${zone.timeframe.toUpperCase()} (${zone.ageMinutes}m)`;
          const fontSize = 9 * pixelRatio;

          ctx.font = `bold ${fontSize}px Arial`;
          const textWidth = ctx.measureText(badgeText).width;
          const badgePadding = 3 * pixelRatio;
          const badgeWidth = textWidth + badgePadding * 2;
          const badgeHeight = fontSize + badgePadding;

          // Position badge horizontally at zone right edge, clamped to stay within canvas
          const rightBound = scope.bitmapSize.width - (8 * pixelRatio); // leave 8px clear for price axis edge
          const preferredX = x2 - badgeWidth - 2 * pixelRatio;
          let badgeX = Math.max(
            x1 + 2 * pixelRatio, // don't escape left edge of narrow zones
            Math.min(preferredX, rightBound - badgeWidth)
          );

          // Collision detection: shift badge left when it overlaps an S/R label
          // S/R labels sit at `right: 58px` in the DOM (~105px wide, ~34px tall).
          // A badge near the right edge of the canvas will visually overlap them.
          const SR_LABEL_HALF_HEIGHT = 17; // ~34px tall labels, centered via translateY(-50%)
          const SR_LABEL_WIDTH = 110 * pixelRatio; // widest SR labels are ~105px
          const COLLISION_MARGIN = 4 * pixelRatio;
          const badgeCenterY_css = (y1 + (height - badgeHeight) / 2 + badgeHeight / 2) / verticalPixelRatio;
          const rightEdgeThreshold = rightBound - badgeWidth - SR_LABEL_WIDTH;
          if (badgeX > rightEdgeThreshold && this._srLabelYPositions.length > 0) {
            for (const srY of this._srLabelYPositions) {
              if (Math.abs(badgeCenterY_css - srY) < SR_LABEL_HALF_HEIGHT + (badgeHeight / verticalPixelRatio / 2)) {
                badgeX = Math.max(x1 + 2 * pixelRatio, rightEdgeThreshold - COLLISION_MARGIN);
                break;
              }
            }
          }
          // Center badge vertically on zone midpoint
          const badgeY = y1 + (height - badgeHeight) / 2;

          // Draw badge background
          ctx.fillStyle = badgeColor;
          ctx.beginPath();
          ctx.roundRect(badgeX, badgeY, badgeWidth, badgeHeight, 2 * pixelRatio);
          ctx.fill();

          // Draw badge text
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(badgeText, badgeX + badgeWidth / 2, badgeY + badgeHeight / 2);

          if (zone.isInvalidated) {
            const strikeY = badgeY + badgeHeight / 2;
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 1 * pixelRatio;
            ctx.beginPath();
            ctx.moveTo(badgeX + 2 * pixelRatio, strikeY);
            ctx.lineTo(badgeX + badgeWidth - 2 * pixelRatio, strikeY);
            ctx.stroke();
          }
        }
      }
    });
  }
}

class FVGZonePaneView implements IPrimitivePaneView {
  private _source: FVGZonePrimitive;
  private _renderer: FVGZonePaneRenderer;

  constructor(source: FVGZonePrimitive) {
    this._source = source;
    this._renderer = new FVGZonePaneRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'bottom'; // Draw behind candles
  }

  renderer(): IPrimitivePaneRenderer {
    const data = this._source.getRendererData();
    this._renderer.update(data, this._source.showTimeframeBadge, this._source.showInvalidatedBadges, this._source.srLabelYPositions);
    return this._renderer;
  }
}

export class FVGZonePrimitive implements ISeriesPrimitive<Time> {
  private _zones: FVGZone[] = [];
  private _paneView: FVGZonePaneView;
  private _requestUpdate?: () => void;
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _rendererData: FVGZoneRendererData[] = [];

  // Configuration
  public showTimeframeBadge: boolean = true;
  public showInvalidatedBadges: boolean = true;
  public visibleTimeframes: Set<ICTTimeframe> = new Set(['2m', '5m', '15m', '1h', '4h']);
  public showFilledZones: boolean = true;
  public showIFVGZones: boolean = true;
  public showFVGZones: boolean = true;
  public showInvalidatedIFVG: boolean = true;

  // FVG colors and opacity
  public fvgBullishColor: string = '#22c55e';
  public fvgBearishColor: string = '#ef4444';
  public fvgFilledOpacity: number = 0.2;
  public fvgUnfilledOpacity: number = 0.4;

  // S/R label collision avoidance — CSS-pixel Y centers of visible S/R labels
  public srLabelYPositions: number[] = [];

  // IFVG colors and opacity
  public ifvgBullishColor: string = '#3b82f6';
  public ifvgBearishColor: string = '#f97316';
  public ifvgFilledOpacity: number = 0.2;
  public ifvgUnfilledOpacity: number = 0.4;

  constructor() {
    this._paneView = new FVGZonePaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._chart = param.chart;
    this._series = param.series;
    this._requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this._chart = undefined;
    this._series = undefined;
    this._requestUpdate = undefined;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {
    // Called when chart needs to update views
  }

  /**
   * Set the FVG zones to display
   */
  setZones(zones: FVGZone[]): void {
    this._zones = zones;
    this._requestUpdate?.();
  }

  /**
   * Set S/R label Y positions for collision avoidance
   */
  setSRLabelPositions(yPositions: number[]): void {
    this.srLabelYPositions = yPositions;
    this._requestUpdate?.();
  }

  /**
   * Add a single zone (for real-time updates)
   */
  addZone(zone: FVGZone): void {
    // Check if zone already exists
    const existingIndex = this._zones.findIndex(z => z.id === zone.id);
    if (existingIndex >= 0) {
      this._zones[existingIndex] = zone;
    } else {
      this._zones.push(zone);
    }
    this._requestUpdate?.();
  }

  /**
   * Update an existing zone (e.g., when fill percentage changes)
   */
  updateZone(zone: FVGZone): void {
    const index = this._zones.findIndex(z => z.id === zone.id);
    if (index >= 0) {
      this._zones[index] = zone;
      this._requestUpdate?.();
    }
  }

  /**
   * Remove a zone by ID
   */
  removeZone(zoneId: string): void {
    this._zones = this._zones.filter(z => z.id !== zoneId);
    this._requestUpdate?.();
  }

  /**
   * Clear all zones
   */
  clearZones(): void {
    this._zones = [];
    this._requestUpdate?.();
  }

  /**
   * Set visible timeframes filter
   */
  setVisibleTimeframes(timeframes: ICTTimeframe[]): void {
    this._visibleTimeframes = new Set(timeframes);
    this._requestUpdate?.();
  }

  private get _visibleTimeframes(): Set<ICTTimeframe> {
    return this.visibleTimeframes;
  }

  private set _visibleTimeframes(value: Set<ICTTimeframe>) {
    this.visibleTimeframes = value;
  }

  /**
   * Check if a click hits any zone
   * Returns the zone if hit, null otherwise
   */
  checkClick(clickX: number, clickY: number): FVGZone | null {
    for (let i = 0; i < this._rendererData.length; i++) {
      const data = this._rendererData[i];
      if (
        clickX >= data.x1 &&
        clickX <= data.x2 &&
        clickY >= Math.min(data.y1, data.y2) &&
        clickY <= Math.max(data.y1, data.y2)
      ) {
        return this._zones.find(z =>
          z.timeframe === data.timeframe &&
          z.zoneType === data.zoneType &&
          z.isFilled === data.isFilled
        ) || null;
      }
    }
    return null;
  }

  /**
   * Get pre-calculated renderer data with coordinates
   */
  getRendererData(): FVGZoneRendererData[] {
    if (!this._chart || !this._series) return [];

    const timeScale = this._chart.timeScale();
    const priceScale = this._series.priceScale();
    const data: FVGZoneRendererData[] = [];

    // Get visible range to limit rendering
    const visibleRange = timeScale.getVisibleRange();
    if (!visibleRange) return [];

    const visibleFrom = (visibleRange.from as number) * 1000; // Convert to ms
    const visibleTo = (visibleRange.to as number) * 1000;
    const currentTime = Date.now();
    const chartWidth = timeScale.width();

    for (const zone of this._zones) {
      // Filter by timeframe
      if (!this.visibleTimeframes.has(zone.timeframe)) continue;

      // Filter filled zones if configured
      // "Show filled" filter applies only to non-inverse zones. IFVG zones are inherently filled (definition) and are gated by showIFVGZones below.
      if (!this.showFilledZones && zone.isFilled && !zone.isInverse) continue;

      // Filter IFVG zones if configured
      if (!this.showIFVGZones && zone.isInverse) continue;

      // Filter FVG zones if configured
      if (!this.showFVGZones && !zone.isInverse) continue;

      // Hide invalidated IFVG zones (those that have been closed through) if user opts out
      if (!this.showInvalidatedIFVG && zone.isInverse && zone.endTime != null) continue;

      // Skip zones that are completely out of view
      const zoneStart = zone.startTime;
      const zoneEnd = zone.endTime || currentTime;
      if (zoneEnd < visibleFrom || zoneStart > visibleTo) continue;

      // Convert times to coordinates
      const startTimeSeconds = Math.floor(zone.startTime / 1000) as Time;
      const endTimeSeconds = Math.floor((zone.endTime || currentTime) / 1000) as Time;

      let x1Coord = timeScale.timeToCoordinate(startTimeSeconds);
      let x2Coord = timeScale.timeToCoordinate(endTimeSeconds);

      // Handle future times or missing coordinates via extrapolation
      if (x1Coord === null || x2Coord === null) {
        const rangeStart = visibleRange.from as number;
        const rangeEnd = visibleRange.to as number;
        const timeRange = rangeEnd - rangeStart;

        if (timeRange > 0) {
          if (x1Coord === null) {
            const relativePos = ((startTimeSeconds as number) - rangeStart) / timeRange;
            x1Coord = (relativePos * chartWidth) as any;
          }
          if (x2Coord === null) {
            const relativePos = ((endTimeSeconds as number) - rangeStart) / timeRange;
            x2Coord = (relativePos * chartWidth) as any;
          }
        }
      }

      if (x1Coord === null || x2Coord === null) continue;

      // Extend unfilled zones to the right edge of the chart
      let x1 = x1Coord as number;
      let x2 = x2Coord as number;
      if (!zone.isFilled && !zone.endTime) {
        x2 = chartWidth;
      }

      // Convert prices to coordinates
      const y1Coord = this._series.priceToCoordinate(zone.highPrice);
      const y2Coord = this._series.priceToCoordinate(zone.lowPrice);

      if (y1Coord === null || y2Coord === null) continue;

      const baseColor = zone.isInverse
        ? (zone.zoneType === 'bullish' ? this.ifvgBullishColor : this.ifvgBearishColor)
        : (zone.zoneType === 'bullish' ? this.fvgBullishColor : this.fvgBearishColor);
      const opacity = zone.isInverse
        ? (zone.isFilled ? this.ifvgFilledOpacity : this.ifvgUnfilledOpacity)
        : (zone.isFilled ? this.fvgFilledOpacity : this.fvgUnfilledOpacity);
      const borderOpacity = Math.min(1, opacity * 2);

      const referenceTime = zone.endTime != null ? zone.endTime : currentTime;
      const ageMinutes = Math.max(0, Math.round((referenceTime - zone.startTime) / 60000));

      data.push({
        x1,
        x2,
        y1: y1Coord as number,
        y2: y2Coord as number,
        zoneType: zone.zoneType,
        isInverse: zone.isInverse,
        isInvalidated: zone.isInverse && zone.endTime != null,
        isFilled: zone.isFilled,
        fillPercentage: zone.fillPercentage,
        timeframe: zone.timeframe,
        fillColor: hexToRgba(baseColor, opacity),
        borderColor: hexToRgba(baseColor, borderOpacity),
        ageMinutes,
      });
    }

    // Cache for hit testing
    this._rendererData = data;

    return data;
  }
}
